import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Alert,
  Link,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { toValidator } from 'renderer/shared/schemas/common.schemas';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import {
  useLoginMutation,
  useSendCodeMutation,
} from '../services/auth.endpoints';
import {
  EnrollFormSchema,
  LoginFormSchema,
  VerifyCodeFormSchema,
} from '../schemas/auth.schemas';
import Credentials from './Credentials';
import EnrollForm from './EnrollForm';
import SendCode from './SendCode';
import VerifyCodeForm from './VerifyCode';
import DAlogo from './da-logo.png';
import DAlogo2 from './da-logo2.png';
import DAlogo3 from './da-logo3.png';

type Steps = 'credentials' | 'enroll' | 'send' | 'verify' | 'success';

const initialForm = {
  email: '',
  password: '',
  phone: '',
  code: '',
};

const Login: React.FC = () => {
  const [
    login,
    { isLoading: isLoginLoading, error: loginError, data: loginResult },
  ] = useLoginMutation();
  const [
    sendCode,
    {
      isLoading: isSendCodeLoading,
      error: sendCodeError,
      isSuccess: isSendCodeSuccess,
    },
  ] = useSendCodeMutation();

  const [step, setStep] = useState<Steps>('credentials');
  const [openInfo, setOpenInfo] = useState(false);

  const navigate = useNavigate();

  const isLoading = isLoginLoading || isSendCodeLoading;

  const phoneToSend =
    loginResult && loginResult.security ? loginResult.security.sms : 'XXXX';

  useEffect(() => {
    if (!loginResult) return;
    if (loginResult.user) {
      setStep('success');
      navigate('/');
      return;
    }
    if (!loginResult.security) return;
    if (loginResult.security.sms === 'MISSING') {
      setStep('enroll');
      return;
    }
    setStep('send');
  }, [loginResult, navigate]);

  useEffect(() => {
    if (isSendCodeSuccess) {
      setStep('verify');
    }
  }, [isSendCodeSuccess]);

  const handleCancel = () => {
    navigate('/');
    // resetForm();
  };

  const handleSubmit = (values: typeof initialForm) => {
    login(values);
  };

  const requestCode = (values: typeof initialForm) => {
    sendCode({ ...values, username: values.email });
  };

  const validate = (values: typeof initialForm) => {
    switch (step) {
      case 'credentials':
        return toValidator(LoginFormSchema)(values);
      case 'enroll':
        return toValidator(EnrollFormSchema)(values);
      case 'verify':
        return toValidator(VerifyCodeFormSchema)(values);
      default:
        return {};
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center', // center the whole column vertically
        alignItems: 'center', // center horizontally
        // gap: 2, // <— tight spacing between banner & card
        gap: 5,
        px: 2,
        // mt: -11,
        mt: { xs: 0, sm: -6, md: -11 },
      }}
    >
      {/* Banner */}
      {/* <Alert
        severity="error"
        sx={{
          px: 2,
          py: 1.5,
          border: '1px solid',
          borderColor: 'error.dark',
          borderRadius: '5px',
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          We have a new store and license management system! Learn more before
          you try to log in.
        </Typography>
        <Typography variant="body2" paragraph>
          Digital Anarchy has a new store and license management system!
        </Typography>
        <Typography variant="body2" paragraph>
          If your account was created on{' '}
          <Link
            href="http://store.digitalanarchy.com"
            target="_blank"
            rel="noopener"
          >
            store.digitalanarchy.com
          </Link>
          , please note that older purchases may still be associated with that
          system. Log in there to view and access previous purchases.
        </Typography>
        <Typography variant="body2" paragraph>
          We recommend creating a new account here on{' '}
          <Link
            href="http://cloud.digitalanarchy.com"
            target="_blank"
            rel="noopener"
          >
            cloud.digitalanarchy.com
          </Link>{' '}
          and adding your serial numbers to your Dashboard. This new system
          simplifies plugin authorization, keeps all your purchases in one
          place, and ensures that any new purchases made while logged in are
          automatically added to your account for easy access and long-term
          storage.
        </Typography>
        <Typography variant="body2">
          Thank you for choosing our products! For assistance, please email us
          at{' '}
          <Link href="mailto:cs@digitalanarchy.com">cs@digitalanarchy.com</Link>
          .
        </Typography>
      </Alert> */}

      <Box
        sx={{
          // mt: { xs: 0, md: -1 },
          mt: { xs: 0, sm: -12, md: -20 },
          mb: 2,
          px: { xs: 1, sm: 2 },
          py: { xs: 1, sm: 1.25 },
          backgroundColor: '#ffebee', // light red background
          color: '#b71c1c', // dark red text
          border: '1px solid #ef9a9a',
          borderRadius: 1,
          boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.2,
          textAlign: 'center',
          '&:hover': {
            backgroundColor: '#ffe5e7',
          },
        }}
        onClick={() => setOpenInfo(true)}
      >
        <Typography sx={{ fontWeight: 600 }}>
          We have a new store and license management system!
        </Typography>
        <Typography sx={{ textDecoration: 'underline', fontWeight: 500 }}>
          Read before you log in.
        </Typography>
      </Box>

      <Paper
        elevation={8}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          borderRadius: 3,
          overflow: 'hidden',
          width: '100%',
          maxWidth: 700,
        }}
      >
        {/* Left: Logo */}
        <Box
          sx={{
            bgcolor: '#ffffff',
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 4,
            borderRight: '1.5px solid #d2d0d0',
          }}
        >
          <img
            src={DAlogo}
            alt="Digital Anarchy Logo"
            style={{ maxWidth: '100%' }}
          />
        </Box>

        {/* Right: Form */}
        <Box sx={{ flex: 1, bgcolor: '#fff', p: 4 }}>
          <Typography
            variant="h5"
            fontWeight="bold"
            gutterBottom
            sx={{ textAlign: 'center' }}
          >
            Log In
          </Typography>

          {(loginError || sendCodeError) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {loginError && formatQueryError(loginError)}
              {sendCodeError && formatQueryError(sendCodeError)}
            </Alert>
          )}

          <Formik
            initialValues={initialForm}
            onSubmit={handleSubmit}
            validate={validate}
          >
            {({ values }) => (
              <Form>
                {step === 'credentials' && <Credentials disabled={isLoading} />}
                {step === 'enroll' && (
                  <EnrollForm
                    disabled={isLoading}
                    sendCode={() => requestCode(values)}
                  />
                )}
                {step === 'send' && (
                  <SendCode
                    disabled={isLoading}
                    phone={phoneToSend}
                    sendCode={() => requestCode(values)}
                  />
                )}
                {step === 'verify' && (
                  <VerifyCodeForm
                    disabled={isLoading}
                    resendCode={() => requestCode(values)}
                  />
                )}
              </Form>
            )}
          </Formik>

          <Box sx={{ mt: 2 }}>
            <Button
              onClick={handleCancel}
              fullWidth
              variant="outlined"
              color="inherit"
            >
              Cancel
            </Button>
          </Box>

          <Typography variant="body2" sx={{ mt: 2 }}>
            <Link
              component={RouterLink}
              to="/forgot-password"
              underline="hover"
              color="primary"
            >
              Click here to reset your password
            </Link>
          </Typography>

          <Typography variant="body2" sx={{ mt: 1 }}>
            Don’t have an account?{' '}
            <Link
              component={RouterLink}
              to="/register"
              underline="hover"
              color="primary"
            >
              Sign up
            </Link>
          </Typography>
        </Box>
      </Paper>
      <Dialog
        open={openInfo}
        onClose={() => setOpenInfo(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>New Store & License Management</DialogTitle>
        <DialogContent dividers>
          <Typography paragraph fontWeight={700}>
            Digital Anarchy has a new store and license management system!
          </Typography>
          <Typography paragraph>
            If your account was created on{' '}
            <Link
              href="http://store.digitalanarchy.com"
              target="_blank"
              rel="noopener"
            >
              store.digitalanarchy.com
            </Link>
            , please note that older purchases may still be associated with that
            system. Log in there to view and access previous purchases.
          </Typography>
          <Typography paragraph>
            We recommend creating a new account here on{' '}
            <Link
              href="http://cloud.digitalanarchy.com"
              target="_blank"
              rel="noopener"
            >
              cloud.digitalanarchy.com
            </Link>{' '}
            and adding your serial numbers to your Dashboard. This new system
            simplifies plugin authorization, keeps all your purchases in one
            place, and ensures that any new purchases made while logged in are
            automatically added to your account for easy access and long-term
            storage.
          </Typography>
          <Typography>
            Thank you for choosing our products! For assistance, please email us
            at{' '}
            <Link href="mailto:cs@digitalanarchy.com">
              cs@digitalanarchy.com
            </Link>
            .
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenInfo(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
